import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, IconCheck } from '@timely/ui';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { Page } from '../workspace/WorkspaceShell';
import { useOnboarding } from '../onboarding/useOnboarding';
import { useSetupStatus, type SetupItem } from '../onboarding/useSetupStatus';
import { paths } from '../workspace/navigation';

/**
 * The onboarding checklist: what a brand-new company sees.
 *
 * Every "done" here is a real question answered against the database — a
 * connected, synced data source; how many people have joined; whether an AI
 * key is saved — never a flag this screen invented. `Today` shows this
 * instead of the day's figures only while the mandatory step (a usable
 * receivables source) is not yet done *and this company has never finished
 * onboarding before* (`useOnboarding`); once it has, an empty book later —
 * after a Fresh Start, say — gets a plain empty state below, not this
 * checklist again.
 */
export function WorkspaceHome() {
    const context = useActiveWorkspace();
    const { workspace, membership } = context;
    const onboarding = useOnboarding(workspace.id);
    const { items, loading, mandatoryDone } = useSetupStatus(context);

    // The moment mandatory setup is first true, record it — once, durably —
    // so this checklist does not reappear the next time the book is empty
    // for an ordinary reason.
    useEffect(() => {
        if (mandatoryDone && !onboarding.completedAt && !onboarding.loading) {
            void onboarding.markComplete();
        }
    }, [mandatoryDone, onboarding.completedAt, onboarding.loading, onboarding.markComplete]);

    const required = items.filter(i => i.required);
    const optional = items.filter(i => !i.required);

    return (
        <Page width="form">
            <h2 className="font-display stretch-title text-title font-semibold text-label tracking-title leading-tight">
                Welcome to Timely Payment
            </h2>
            <p className="text-subhead text-label-2 mt-2 leading-relaxed">
                {workspace.name} is set up and you are its {membership.roleName.toLowerCase()}.
                Here is what is left.
            </p>

            {/* The one required step is the one surface; the optional ones are a plain list under it, and the empty
                book is a sentence, not a third box saying what the first already says (2026-10 global sweep). */}
            <Card className="mt-7 divide-y divide-separator overflow-hidden">
                {required.map(item => <ChecklistRow key={item.area} item={item} loading={loading} />)}
            </Card>
            <p className="mt-3 text-secondary text-label-3">
                Customers appear here once your receivables are connected. Nothing is invented in the meantime.
            </p>

            {!loading && optional.length > 0 && (
                <>
                    <h3 className="text-secondary font-semibold text-label-3 mt-10 mb-1">
                        Optional — do these whenever you like
                    </h3>
                    <div className="divide-y divide-separator border-b border-separator">
                        {optional.map(item => <ChecklistRow key={item.area} item={item} loading={loading} flush />)}
                    </div>
                    <p className="text-secondary mt-4">
                        <Link to={paths(workspace.slug).setting('setup')} className="text-accent-text font-semibold hover:underline underline-offset-4">
                            See the full company setup status
                        </Link>
                    </p>
                </>
            )}
        </Page>
    );
}

function ChecklistRow({ item, loading, flush = false }: { item: SetupItem; loading: boolean; flush?: boolean }) {
    const done = item.state === 'configured';
    const attention = item.state === 'needs_attention';

    return (
        <div className={`${flush ? 'py-3.5' : 'px-5 py-4'} flex items-start gap-3.5`}>
            {/* The step's mark: a jade tick when done, the warning square when it needs attention, an open square
                still to do — the product's settle square, not a disc that reads as a radio button. */}
            <span className="size-5 grid place-items-center flex-none mt-0.5" aria-hidden="true">
                {!loading && done ? <span className="text-pos"><IconCheck size={15} /></span>
                    : !loading && attention ? <span className="size-2.5 bg-warn" />
                    : <span className="size-2.5 border-[1.5px] border-label-4" />}
            </span>
            <div className="min-w-0">
                <div className="text-subhead font-semibold text-label">
                    {item.can && item.href ? (
                        <Link to={item.href} className="text-accent-text hover:underline underline-offset-4">{item.label}</Link>
                    ) : (
                        item.label
                    )}
                </div>
                <div className="text-secondary text-label-3 mt-0.5 leading-snug">
                    {loading ? 'Checking…' : item.detail}
                </div>
            </div>
        </div>
    );
}
